import React, { useEffect, useState } from "react";
import { Card, BlockStack, InlineStack, Text, DataTable, Spinner, Divider, Badge } from "@shopify/polaris";
import { GetApiCall } from "../../helper/axios";

/*
 * "Sales by currency" - real order revenue grouped by the currency the customer
 * checked out in (Shopify presentment currency), summed in the shop's base /
 * payout currency. Data comes from GET /analytics/sales-by-currency, which reads
 * orders from the Admin API for the same window as the rest of the dashboard.
 */
const fmtMoney = (amount, cur) => {
    const n = Number(amount) || 0;
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: cur || "USD", maximumFractionDigits: 2 }).format(n);
    } catch (e) {
        return `${n.toFixed(2)} ${cur || ""}`.trim();
    }
};

function SalesByCurrency({ token, query }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!token || !query) return;
        let alive = true;
        setLoading(true);
        GetApiCall("GET", `/analytics/sales-by-currency?${query}`, { authentication: token })
            .then((res) => { if (alive && res?.data?.statusCode === 200 && res?.data?.data) setData(res.data.data); })
            .catch(() => { /* keep the dashboard usable even if orders can't load */ })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, query]);

    const rows = data?.rows || [];
    const base = data?.baseCurrency || "";
    // Rows are grouped by the customer's checkout / region currency; each total is
    // shown in the shop's base/payout currency so they're directly comparable.
    const tableRows = rows.map((r) => [r.currency, fmtMoney(r.sales, base)]);

    const subtitle = data && rows.length
        ? `${fmtMoney(data.totalSales, base)} from ${data.totalOrders} order${data.totalOrders === 1 ? "" : "s"}`
        : "Revenue grouped by checkout currency";

    return (
        <Card>
            <BlockStack gap="300">
                <BlockStack gap="050">
                    <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
                        <Text as="h2" variant="headingMd">Sales by currency</Text>
                        {data?.truncated ? <Badge tone="attention">Recent orders</Badge> : null}
                    </InlineStack>
                    <Text as="span" variant="bodySm" tone="subdued">{subtitle}</Text>
                </BlockStack>
                <Divider />
                {loading && !data ? (
                    <div style={{ textAlign: "center", padding: "28px 0" }}>
                        <Spinner accessibilityLabel="Loading sales" size="small" />
                    </div>
                ) : rows.length ? (
                    <DataTable
                        columnContentTypes={["text", "numeric"]}
                        headings={["Currency", "Sales"]}
                        rows={tableRows}
                    />
                ) : (
                    <Text as="span" tone="subdued">
                        {data?.error ? "Couldn't load order data right now." : "No sales in this range yet."}
                    </Text>
                )}
            </BlockStack>
        </Card>
    );
}

export default SalesByCurrency;
